import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { Role } from '@/services/rolesPermissionsService';

interface DeleteRoleModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  roleToDelete: Role | null;
  onConfirm: () => void;
  isLoading: boolean;
}

export const DeleteRoleModal: React.FC<DeleteRoleModalProps> = ({
  isOpen,
  onOpenChange,
  roleToDelete,
  onConfirm,
  isLoading
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            Confirmar eliminación
          </DialogTitle>
          <DialogDescription>
            Esta acción no se puede deshacer. El rol y todas sus asignaciones serán eliminados permanentemente.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {roleToDelete?.is_protected ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
              <div className="text-sm">
                <p className="font-medium text-amber-800">
                  ⚠️ Este rol está protegido y no puede ser eliminado
                </p>
                <p className="mt-1 text-amber-700">
                  <strong>{roleToDelete?.display_name || roleToDelete?.name}</strong>
                </p>
                <p className="mt-2 text-amber-600 text-xs">
                  Los roles protegidos son críticos para el funcionamiento del sistema y no pueden ser eliminados.
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="text-sm">
                <p className="font-medium text-red-800">
                  ¿Estás seguro de que quieres eliminar el rol?
                </p>
                <p className="mt-1 text-red-700">
                  <strong>{roleToDelete?.display_name || roleToDelete?.name}</strong>
                </p>
                {roleToDelete?.description && (
                  <p className="mt-1 text-red-600 text-xs">
                    {roleToDelete.description}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              {roleToDelete?.is_protected ? 'Cerrar' : 'Cancelar'}
            </Button>
            {!roleToDelete?.is_protected && (
              <Button 
                variant="destructive"
                onClick={onConfirm}
                disabled={isLoading}
              >
                {isLoading ? 'Eliminando...' : 'Sí, eliminar rol'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};